/**
 * Color Pin component
 *
 * Owns row ordering for the pricing grid while "Show Every Color" is checked
 * (i.e. single color mode is OFF). The selected color's row group is hoisted to
 * row 1 and every other color keeps its server `sorted_family` position
 * (Black → White → alphabetical).
 *
 * Exactly one color is ever pinned. Selecting another color sends the outgoing
 * one back to the slot family order gives it, so the table is never more than
 * one row group away from canonical order.
 *
 * This module deliberately imports nothing from singleColorMode or
 * lazyProductLoader: both of those need to read the pin (the lazy loader has to
 * know row 1 is not a valid insertion anchor), and a cycle between row-ordering
 * modules is the kind of thing that breaks silently at bundle time. Callers own
 * the mode check — colorFilter already branches on isSingleColorModeActive().
 */

import { debounce } from "../utils/debounce.js";

/**
 * Canonical row order, captured once before anything reorders the tbody.
 * Shared with singleColorMode so both row-moving paths agree on what
 * "original order" means.
 */
const familyRowOrder: string[] = [];

/** Handle currently hoisted to row 1, or null when nothing is pinned. */
let pinnedHandle: string | null = null;

/**
 * Tabindex block width per product. Wider than the 100 used by Liquid and the
 * lazy loader on purpose — see reassignDesktopTabindexes().
 */
const TABINDEX_BLOCK = 200;

/** Tabindex stride between warehouse rows within one product group. */
const TABINDEX_ROW_STRIDE = 20;

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Capture the canonical row order. Must run before anything reorders the tbody
 * — call it immediately after initLazyProductLoader() and before
 * initSingleColorMode().
 *
 * When lazy loading is active the full list is already in window.__productFamily
 * (emitted by Liquid at main-product.liquid:852); that embed is absent on pages
 * where every color is server-rendered, so we fall back to scanning DOM rows.
 */
export function initColorPin(): void {
  const family = window.__productFamily;
  if (Array.isArray(family) && family.length > 0) {
    familyRowOrder.push(...family);
    return;
  }

  const seen = new Set<string>();
  $(".warehouse_row").each(function (): void {
    const handle = $(this).attr("data-product-handle");
    if (handle && !seen.has(handle)) {
      seen.add(handle);
      familyRowOrder.push(handle);
    }
  });
}

/** The canonical row order captured at init. */
export function getFamilyRowOrder(): string[] {
  return familyRowOrder;
}

/** Handle currently pinned to row 1, or null when nothing is pinned. */
export function getPinnedHandle(): string | null {
  return pinnedHandle;
}

/**
 * Hoist a color's row group to row 1, returning any previously pinned color to
 * its family position. Idempotent: re-pinning the current pin just re-asserts
 * the hoist.
 *
 * No-op when the color's rows aren't in the DOM yet — callers must await
 * ensureProductLoaded() first.
 */
export function pinColorToTop(handle: string): void {
  const $tbody = $(".inventory_table_tbody");
  if ($tbody.length === 0) return;

  const $rows = $(`.warehouse_row[data-product-handle="${handle}"]`);
  if ($rows.length === 0) {
    console.warn("[colorPin] No rows loaded for handle=%s — not pinning", handle);
    return;
  }

  // Send the outgoing pin home before hoisting the new one. Order matters: the
  // incoming handle is still sitting in its own family slot at this point, so
  // it stays a valid anchor for the outgoing group.
  if (pinnedHandle !== null && pinnedHandle !== handle) {
    restoreHandleToFamilyPosition(pinnedHandle);
  }

  // prependTo on a matched set preserves the set's document order, so the
  // group's first row — the one carrying the rowspan'd image and subtotal
  // cells (product-inventory-row.liquid:328, :474) — stays first.
  $rows.prependTo($tbody);
  pinnedHandle = handle;

  scheduleDesktopTabindexReassign();
}

/**
 * Re-hoist the pinned group if a lazy insert has landed above it.
 * Cheap no-op when the pin is already row 1.
 *
 * insertRows() positions new groups correctly on its own, so in practice this
 * never fires. It stays as a cheap guarantee of the invariant: any future path
 * that prepends rows can't strand the pin below them.
 */
export function reassertPin(): void {
  if (pinnedHandle === null) return;

  const $tbody = $(".inventory_table_tbody");
  if ($tbody.length === 0) return;

  const firstHandle = $tbody
    .children("tr.warehouse_row")
    .first()
    .attr("data-product-handle");
  if (firstHandle === pinnedHandle) return;

  $(`.warehouse_row[data-product-handle="${pinnedHandle}"]`).prependTo($tbody);
  scheduleDesktopTabindexReassign();
}

/**
 * Forget the pin without touching the DOM.
 *
 * For callers that rebuild the whole row order themselves — single color mode's
 * applySingleColorMode() re-appends every group, so restoring the pinned group
 * first would just be work its own reorder immediately overwrites.
 */
export function clearPin(): void {
  pinnedHandle = null;
}

/**
 * The color handle the user deep-linked to, or null if this wasn't a deep link.
 *
 * Search results and cart links land on `/products/{handle}?variant={id}`, where
 * the handle *is* the color. The `variant` param is what distinguishes a deep
 * link from ordinary browsing, so plain navigation leaves row order alone.
 *
 * Callers pin this without scrolling: search sends users into product pages
 * constantly, and jumping the viewport down to the grid on every arrival would
 * be hostile. The cart's own scroll is a separate, flagged path
 * (scrollToVariant.ts:21) and is untouched.
 */
export function getDeepLinkedHandle(): string | null {
  if (!new URL(window.location.href).searchParams.has("variant")) return null;

  const match = /\/products\/([^/?#]+)/.exec(window.location.pathname);
  if (!match) return null;

  const handle = decodeURIComponent(match[1]);
  return familyRowOrder.length === 0 || familyRowOrder.includes(handle)
    ? handle
    : null;
}

/**
 * Rewrite tabindex on every desktop quantity input from current DOM order.
 *
 * Liquid derives tabindex from `product_index`
 * (product-inventory-row.liquid:48), so tab order tracks family order rather
 * than what's on screen. The moment a color is pinned to row 1 the two diverge,
 * and tabbing out of the pinned group jumps to whichever color happens to own
 * the next numeric block instead of the row visibly below it.
 *
 * The 200-wide block is not arbitrary: a product can render six warehouse rows
 * and the row stride is 20, so a product needs 120 slots. The 100-wide block
 * Liquid and lazyProductLoader both use has warehouse 6 overlapping the next
 * product's local row. Reassigning from DOM order clears that up as a side
 * effect.
 */
export function reassignDesktopTabindexes(): void {
  const rows = Array.from(
    document.querySelectorAll<HTMLElement>(
      ".inventory_table_tbody > tr.warehouse_row",
    ),
  );
  if (rows.length === 0) return;

  let currentHandle: string | null = null;
  let groupIndex = -1;
  let rowInGroup = 0;

  rows.forEach((row) => {
    const handle = row.dataset.productHandle ?? "";
    if (handle !== currentHandle) {
      currentHandle = handle;
      groupIndex += 1;
      rowInGroup = 0;
    }

    const base =
      (groupIndex + 1) * TABINDEX_BLOCK + rowInGroup * TABINDEX_ROW_STRIDE;

    row
      .querySelectorAll<HTMLInputElement>(".variant_qty_input_directship")
      .forEach((input, variantIndex) => {
        input.tabIndex = base + variantIndex + 1;
      });

    rowInGroup += 1;
  });
}

/**
 * Debounced reassignment. Lazy loading streams rows in one product at a time —
 * on a large family that's 100+ inserts, and each one shifts every subsequent
 * group's DOM index. Reassigning once the inserts settle is far cheaper than
 * rewriting the whole grid's tabindexes per insert.
 */
export const scheduleDesktopTabindexReassign: () => void = debounce(
  reassignDesktopTabindexes,
  150,
);

// ─── Private helpers ─────────────────────────────────────────────────────────

/**
 * Move a product's row group back to the slot family order gives it, anchored
 * on the nearest preceding handle that is actually in the DOM.
 *
 * Mirrors lazyProductLoader's insertion logic so a restored group lands exactly
 * where a freshly loaded one would.
 */
function restoreHandleToFamilyPosition(handle: string): void {
  const $tbody = $(".inventory_table_tbody");
  const $rows = $(`.warehouse_row[data-product-handle="${handle}"]`);
  if ($rows.length === 0) return;

  const idx = familyRowOrder.indexOf(handle);
  if (idx < 0) {
    // Unknown to family order (shouldn't happen) — park it at the end rather
    // than guessing a position.
    $rows.appendTo($tbody);
    return;
  }

  for (let i = idx - 1; i >= 0; i--) {
    const prevHandle = familyRowOrder[i];
    // Never anchor on a hoisted group: row 1 is not a family position.
    if (prevHandle === handle || prevHandle === pinnedHandle) continue;

    const $prevRows = $(`.warehouse_row[data-product-handle="${prevHandle}"]`);
    if ($prevRows.length > 0) {
      $rows.insertAfter($prevRows.last());
      return;
    }
  }

  $rows.prependTo($tbody);
}
